/**
 * Pure permission helpers for GearSync integrations. Safe to import from
 * both client and server code — no Firestore reads, no Node-only deps.
 *
 * Default-deny semantics: a missing `permissions.integrations` field on the
 * user doc (existing users) is treated as "no access" for everything.
 */

export type IntegrationProvider = 'ebay' | 'lightspeed-retail';

export type IntegrationsPermissions =
  | {
      enabled?: boolean;
      ebay?: boolean;
      'lightspeed-retail'?: boolean;
    }
  | null
  | undefined;

/** True if the user can see/use the integrations dashboard at all. */
export function canUseIntegrations(perms: IntegrationsPermissions): boolean {
  return Boolean(perms?.enabled);
}

/**
 * True if the user can connect/manage the given provider. Master `enabled`
 * must also be true — per-provider access is meaningless without it.
 */
export function canUseProvider(
  perms: IntegrationsPermissions,
  provider: IntegrationProvider
): boolean {
  if (!canUseIntegrations(perms)) return false;
  return Boolean(perms?.[provider]);
}

/** The subset of providers the user is allowed to use. */
export function allowedProviders(
  perms: IntegrationsPermissions
): IntegrationProvider[] {
  if (!canUseIntegrations(perms)) return [];
  const all: IntegrationProvider[] = ['ebay', 'lightspeed-retail'];
  return all.filter((p) => Boolean(perms?.[p]));
}

/**
 * Narrow an untrusted `?provider=` query/body value to a known provider.
 * Returns null for unknown values (caller should respond 400). Missing/empty
 * defaults to `lightspeed-retail` for back-compat with the old proxy.
 */
export function parseProvider(raw: unknown): IntegrationProvider | null {
  if (raw === undefined || raw === '') return 'lightspeed-retail';
  if (raw === 'ebay' || raw === 'lightspeed-retail') return raw;
  return null;
}
